import {cardTypeToName} from '../../../campaignSetupAgent.config';
import {conditionCardSchema} from '../../../schemas/conditionCardSchema';
import {creativeCardSchema} from '../../../schemas/creativeCardSchema';
import {optimizationCardSchema} from '../../../schemas/optimizationCardSchema';
import {scheduleCardSchema} from '../../../schemas/scheduleCardSchema';
import {weightedCardSchema} from '../../../schemas/weightedCardSchema';
import promptToCardAgent, {
  PromptToCardAgentStatus,
} from '../../../standaloneAgents/promptToCardAgent/promptToCardAgent';

import {CAMPAIGN_NODE_ID} from 'common/src/components/CLCardsMap/CLCardsMap.config';
import {
  ContentFeedNode,
  DTCreativeNode,
  DTDistributionNode,
  DTOptimizationNode,
  DTProductSelectionNode,
  DTStoreNode,
  DTWeightedNode,
} from 'common/src/modules/decisionTree/decisionTree.types';
import {NodeType} from 'common/src/modules/nodes/nodes.types';
import {Tool, ToolExecuteParams} from 'common/src/modules/shared/copilot/copilot.types';
import tool from 'common/src/modules/shared/copilot/utils/creators/tool';
import toolResult from 'common/src/modules/shared/copilot/utils/creators/toolResult';
import {CLStore} from 'common/src/redux/store';
import {DateTime} from 'luxon';
import {ZodTypeAny} from 'zod';
import {addOrUpdateCardInputSchema} from '../../../schemas/baseSchemas';
import {contentCardSchema} from '../../../schemas/contentCardSchema';
import {locationCardSchema} from '../../../schemas/locationCardSchema';
import {productCardSchema} from '../../../schemas/productCardSchema';
import {sequenceCardSchema} from '../../../schemas/sequenceCardSchema';
import addOrUpdateNodeState, {AddOrUpdateNodeStateType} from '../../../utils/addOrUpdateNodeState';
import convertCardSchemaToCard from './utils/convertCardSchemaToCard/convertCardSchemaToCard';
import getAddOrUpdateCardPrompt from './utils/getAddOrUpdateCardPrompt';
import { distributionCardSchema } from '../../../schemas/distributionCardSchema';

type CardSchema =
  | typeof optimizationCardSchema
  | typeof weightedCardSchema
  | typeof creativeCardSchema
  | typeof scheduleCardSchema
  | typeof conditionCardSchema
  | typeof sequenceCardSchema
  | typeof contentCardSchema
  | typeof locationCardSchema
  | typeof productCardSchema
  | typeof distributionCardSchema;

export default function addOrUpdateCardTool<TCardSchema extends CardSchema>({
  store,
  cardSchema,
  cardType,
  tools = [],
  cardSpecificInstructions = '',
  description = '',
}: {
  store: CLStore;
  cardType: NodeType;
  cardSchema: TCardSchema;
  tools?: Tool<ZodTypeAny>[];
  cardSpecificInstructions?: string;
  description?: string;
}) {
  async function execute(params: ToolExecuteParams<typeof addOrUpdateCardInputSchema>) {
    const {prompt, cardId: cardIdInput = 0} = params.input;
    const {toolUtils, callChain} = params;

    const NO_NODE_ID = 0; // used to indicate a new card

    const cardId = Math.max(NO_NODE_ID, cardIdInput ?? NO_NODE_ID);

    const promptWithDateInstructions = prompt.concat(
      `\n When using dates, use the ISO format for the dates. 
        Today's date is ${DateTime.now().toISO()}.`,
    );

    //extend base prompt with existing card details
    const promptToUse = getAddOrUpdateCardPrompt({
      prompt: promptWithDateInstructions,
      cardId,
      store,
    });

    //use the standalone agent to generate the card schema
    const result = await toolUtils.invokeStandaloneAgent({
      agent: promptToCardAgent({
        cardType: cardType,
        cardId,
        tools,
        cardSchema,
        cardSpecificInstructions,
      }),
      prompt: promptToUse,
      callChain,
    });

    const {data: aiGeneratedCardSchema, status, chainOfThoughts} = result;

    // If the agent didn't generate a card schema, return no action result
    if (!aiGeneratedCardSchema || status === PromptToCardAgentStatus.NoAction) {
      return toolResult(
        JSON.stringify({status: PromptToCardAgentStatus.NoAction, chainOfThoughts}),
      );
    }

    // Convert the card schema to the corresponding node data
    const cardData = convertCardSchemaToCard(aiGeneratedCardSchema, cardId);

    // Check if the card exists (If its a new card, LLM will give it 0 or 1 id)
    const isExistingCard = cardId > CAMPAIGN_NODE_ID;

    // Add or update the node state
    try {
      await addOrUpdateNodeState(
        store,
        cardData as
          | DTOptimizationNode
          | DTWeightedNode
          | DTCreativeNode
          | ContentFeedNode
          | DTStoreNode
          | DTProductSelectionNode
          | DTDistributionNode,
        isExistingCard ? AddOrUpdateNodeStateType.Update : AddOrUpdateNodeStateType.Add,
      );

      const response = `${isExistingCard ? 'Updated' : 'Added'} ${
        cardTypeToName[cardType]
      } card with id ${cardData.id} successfully. Summary: ${chainOfThoughts}`;

      return toolResult(response);
    } catch (error) {
      return toolResult(
        JSON.stringify({
          status: PromptToCardAgentStatus.NoAction,
          chainOfThoughts,
          error: error.message,
        }),
      );
    }
  }

  const name = `AddOrUpdate${cardTypeToName[cardType]}CardTool`;

  return tool({
    name,
    description: description || `Add or update SINGLE ${cardTypeToName[cardType]} card`,
    displayName: `Working on ${cardTypeToName[cardType]} card`,
    inputSchema: addOrUpdateCardInputSchema,
    execute,
  });
}
